import React, { useEffect, useMemo, useState } from "react";
import SearchableSelect from "../Components/SearchableSelect";
import TextInput from "../Components/TextInput";
import Button from "../Components/Button";
import AlertToast from "../Components/AlertToast";

const MONTHS = [
  { value: 1, label: "Gennaio", key: "Gennaio" },
  { value: 2, label: "Febbraio", key: "Febbraio" },
  { value: 3, label: "Marzo", key: "Marzo" },
  { value: 4, label: "Aprile", key: "Aprile" },
  { value: 5, label: "Maggio", key: "Maggio" },
  { value: 6, label: "Giugno", key: "Giugno" },
  { value: 7, label: "Luglio", key: "Luglio" },
  { value: 8, label: "Agosto", key: "Agosto" },
  { value: 9, label: "Settembre", key: "Settembre" },
  { value: 10, label: "Ottobre", key: "Ottobre" },
  { value: 11, label: "Novembre", key: "Novembre" },
  { value: 12, label: "Dicembre", key: "Dicembre" },
];

function getDefaultYearAndMonth() {
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();

  if (currentMonth === 1) {
    return {
      defaultYear: currentYear,
      defaultMonth: 12,
    };
  }

  return {
    defaultYear: currentYear,
    defaultMonth: currentMonth - 1,
  };
}

function getUserFromToken() {
  const token = localStorage.getItem("token");
  if (!token) return null;

  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload?.id || payload?.userId || payload?.username || null;
  } catch {
    return null;
  }
}

const thStyle = {
  color: "white",
  textAlign: "center",
  padding: "12px 10px",
  fontSize: 13,
  fontWeight: 700,
  borderBottom: "1px solid rgba(255,255,255,0.08)",
  whiteSpace: "nowrap",
};

const tdStyle = {
  textAlign: "center",
  padding: "12px 10px",
  borderBottom: "1px solid rgba(255,255,255,0.06)",
};

const tdNameStyle = {
  ...tdStyle,
  textAlign: "left",
  color: "white",
  fontWeight: 600,
  minWidth: 240,
};

const emptyStyle = {
  textAlign: "center",
  padding: "18px",
  color: "#bdbdbd",
};

const selectStyle = {
  width: "100%",
  minHeight: 42,
  borderRadius: 8,
  border: "1px solid #3a3a3a",
  padding: "10px 12px",
  background: "#1f1f1f",
  color: "white",
};

const RegistraConciliazioneContoCorrentePage = () => {
  const { defaultYear, defaultMonth } = useMemo(() => getDefaultYearAndMonth(), []);

  const initialForm = {
    amministratoreId: "",
    condominioId: "",
    anno: String(defaultYear),
    mese: String(defaultMonth),
  };

  const [form, setForm] = useState(initialForm);
  const [alert, setAlert] = useState(null);

  const [loadingSalvataggio, setLoadingSalvataggio] = useState(false);
  const [loadingAmministratori, setLoadingAmministratori] = useState(false);
  const [loadingCondomini, setLoadingCondomini] = useState(false);
  const [loadingRiepilogo, setLoadingRiepilogo] = useState(false);

  const [amministratori, setAmministratori] = useState([]);
  const [condomini, setCondomini] = useState([]);
  const [riepilogoConciliazione, setRiepilogoConciliazione] = useState([]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => {
      if (name === "amministratoreId") {
        return {
          ...prev,
          amministratoreId: value,
          condominioId: "",
        };
      }

      return {
        ...prev,
        [name]: value,
      };
    });
  };


  async function loadAmministratori() {
    try {
      setLoadingAmministratori(true);
   
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Token mancante");

      const res = await fetch("https://api.lapegna.xyz/conciliazioni/combo_clienti", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `Errore HTTP ${res.status}`);
      }

      const data = await res.json();

      const listaClienti = Array.isArray(data)
        ? data
        : data.data || data.recordset || [];

      setAmministratori(listaClienti);

    } catch (err) {
      console.error(err);
      
    } finally {
      setLoadingAmministratori(false);
    }
  }


  const loadCondomini = async (fkCliente) => {
    if (!fkCliente) {
      setCondomini([]);
      return;
    }

    try {
      setLoadingCondomini(true);

      const token = localStorage.getItem("token");

      const res = await fetch(
        "https://api.lapegna.xyz/conciliazioni/condomini_amministratore",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            Cliente: Number(fkCliente),
          }),
        }
      );

      if (!res.ok) {
        throw new Error("Errore nel caricamento condomini");
      }

      const data = await res.json();
      setCondomini(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setCondomini([]);
    } finally {
      setLoadingCondomini(false);
    }
  };

  useEffect(() => {
  loadCondomini(form.amministratoreId);
  }, [form.amministratoreId]);

  const loadRiepilogoConciliazione = async (condominioId, anno) => {
    if (!condominioId || !anno) {
      setRiepilogoConciliazione([]);
      return;
    }

    try {
      setLoadingRiepilogo(true);

      const token = localStorage.getItem("token");
      const userId = getUserFromToken();

      if (!token || !userId) {
        setRiepilogoConciliazione([]);
        return;
      }

      const res = await fetch("https://api.lapegna.xyz/conciliazioni/mesi_riconciliati", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          Cliente: Number(form.amministratoreId),
          Condominio: condominioId,
          Anno: Number(anno),
        }),
      });

      if (!res.ok) {
        throw new Error("Errore nel caricamento riepilogo conciliazioni");
      }

      const data = await res.json();
      const righe = Array.isArray(data?.data) ? data.data : [];

      setRiepilogoConciliazione(
        righe.filter((r) => String(r.ID) === String(condominioId))
      );
    } catch (err) {
      console.error(err);
      setRiepilogoConciliazione([]);
      setAlert({
        type: "error",
        message: "Errore durante il caricamento del riepilogo conciliazioni",
      });
    } finally {
      setLoadingRiepilogo(false);
    }
  };

  useEffect(() => {
    loadAmministratori();
  }, []);

  useEffect(() => {
    loadRiepilogoConciliazione(form.condominioId, form.anno);
  }, [form.condominioId, form.anno]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.amministratoreId || !form.condominioId || !form.anno || !form.mese) {
      setAlert({
        type: "error",
        message: "Compila Amministratore, Condominio, Anno e Mese.",
      });
      return;
    }

    const token = localStorage.getItem("token");
    const userId = getUserFromToken();

    if (!token || !userId) {
      setAlert({
        type: "error",
        message: "Utente non autenticato",
      });
      return;
    }

    try {
      setLoadingSalvataggio(true);

      const meseConciliato = `${form.anno}-${String(form.mese).padStart(2, "0")}-01`;

      const res = await fetch("https://api.lapegna.xyz/conciliazioni/inserisci_conciliazione", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          FK_CONDOMINIO: Number(form.condominioId),
          MESE_CONCILIATO: meseConciliato,
          CREATE_DATE: new Date().toISOString(),
          CREATE_USER: userId,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || `Errore HTTP ${res.status}`);
      }

      setAlert({
        type: "success",
        message: "Conciliazione registrata correttamente",
      });

      await loadRiepilogoConciliazione(form.condominioId, form.anno);
    } catch (err) {
      console.error(err);
      setAlert({
        type: "error",
        message: "Errore durante il salvataggio della conciliazione",
      });
    } finally {
      setLoadingSalvataggio(false);
    }
  };

  const handleReset = () => {
    setForm(initialForm);
    setCondomini([]);
    setRiepilogoConciliazione([]);
  };

  const rigaSelezionata = riepilogoConciliazione[0] || null;

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>
      <h2>Registra conciliazione conto corrente</h2>

      <form onSubmit={handleSubmit} style={{ display: "grid", gap: 12 }}>
        <label style={{ display: "grid", gap: 6 }}>
          <span style={{ color: "white" }}>Amministratore</span>
        <SearchableSelect
          name="amministratoreId"
          value={form.amministratoreId}
          onChange={handleChange}
          disabled={loadingAmministratori || loadingSalvataggio}
          placeholder={
            loadingAmministratori
              ? "Caricamento..."
              : "-- Seleziona un amministratore --"
          }
          options={amministratori.map((a) => ({
            value: a.ID,
            label: a.RAGIONE_SOCIALE,
          }))}
        />
        </label>

        <label style={{ display: "grid", gap: 6 }}>
          <span style={{ color: "white" }}>Condominio</span>
          <SearchableSelect
            name="condominioId"
            value={form.condominioId}
            onChange={handleChange}
            disabled={
              !form.amministratoreId || loadingCondomini || loadingSalvataggio
            }
            placeholder={
              !form.amministratoreId
                ? "-- Seleziona prima un amministratore --"
                : loadingCondomini
                ? "Caricamento..."
                : "-- Seleziona un condominio --"
            }
            options={condomini.map((c) => ({
              value: c.ID,
              label: c.DENOMINAZIONE,
            }))}
          />
        </label>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <TextInput
            label="Anno"
            name="anno"
            type="number"
            value={form.anno}
            onChange={handleChange}
            placeholder="Anno"
          />

          <label style={{ display: "grid", gap: 6 }}>
            <span style={{ color: "white" }}>Mese</span>
            <select
              name="mese"
              value={form.mese}
              onChange={handleChange}
              disabled={loadingSalvataggio}
              style={selectStyle}
            >
              {MONTHS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
          <Button
            type="submit"
            disabled={
              loadingSalvataggio || loadingAmministratori || loadingCondomini
            }
          >
            {loadingSalvataggio ? "Salvataggio..." : "Registra conciliazione"}
          </Button>

          <Button
            type="button"
            variant="danger"
            onClick={handleReset}
            disabled={loadingSalvataggio}
          >
            Svuota
          </Button>
        </div>

        {form.condominioId && (
          <div
            style={{
              marginTop: 18,
              borderRadius: 12,
              overflow: "hidden",
              border: "1px solid rgba(255,255,255,0.08)",
              background: "rgba(255,255,255,0.03)",
            }}
          >
            <div
              style={{
                padding: "14px 16px",
                borderBottom: "1px solid rgba(255,255,255,0.08)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <div style={{ color: "white", fontWeight: 700, fontSize: 16 }}>
                  Stato conciliazioni anno {form.anno}
                </div>
                <div style={{ color: "#bdbdbd", fontSize: 13 }}>
                  Visualizzazione del solo condominio selezionato
                </div>
              </div>

              {loadingRiepilogo && (
                <span style={{ color: "#bdbdbd", fontSize: 13 }}>
                  Aggiornamento...
                </span>
              )}
            </div>

            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  minWidth: 980,
                }}
              >
                <thead>
                  <tr style={{ background: "rgba(255,255,255,0.05)" }}>
                    <th style={thStyle}>Condominio</th>
                    {MONTHS.map((m) => (
                      <th key={m.key} style={thStyle}>
                        {m.label}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {!loadingRiepilogo && rigaSelezionata && (
                    <tr>
                      <td style={tdNameStyle}>{rigaSelezionata.Denominazione}</td>

                      {MONTHS.map((m) => {
                        const value = rigaSelezionata[m.key];
                        const conciliato = value && Number(value) > 0;

                        return (
                          <td key={m.key} style={tdStyle}>
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minWidth: 36,
                                height: 28,
                                padding: "0 10px",
                                borderRadius: 999,
                                background: conciliato
                                  ? "rgba(34,197,94,0.18)"
                                  : "rgba(255,255,255,0.08)",
                                color: conciliato ? "#86efac" : "#d1d5db",
                                fontWeight: 600,
                                fontSize: 12,
                              }}
                              title={
                                conciliato
                                  ? `ID conciliazione: ${value}`
                                  : "Non conciliato"
                              }
                            >
                              {conciliato ? "OK" : "-"}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  )}

                  {!loadingRiepilogo && !rigaSelezionata && (
                    <tr>
                      <td style={emptyStyle} colSpan={13}>
                        Nessun dato disponibile per il condominio e l'anno selezionati.
                      </td>
                    </tr>
                  )}

                  {loadingRiepilogo && (
                    <tr>
                      <td style={emptyStyle} colSpan={13}>
                        Caricamento riepilogo conciliazioni...
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <AlertToast
          message={alert?.message}
          type={alert?.type}
          onClose={() => setAlert(null)}
        />
      </form>
    </div>
  );
};

export default RegistraConciliazioneContoCorrentePage;